import type { TErrorBody } from '@cascade/api-client/errors';
import { isRecord } from '#utils/object/index.ts';
import { describeDetail } from './errors.helpers.ts';
import type { TErrorCode, TRequestErrorDetail } from './errors.types.ts';

export const statusByErrorCode = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  VALIDATION_ERROR: 422,
  RATE_LIMITED: 429,
  UNAVAILABLE_LEGAL_REASONS: 451,
  INTERNAL_ERROR: 500,
} as const satisfies Record<TErrorCode, number>;

export class RequestError extends Error {
  readonly detail: TRequestErrorDetail;

  constructor(detail: TRequestErrorDetail, options?: ErrorOptions) {
    super(describeDetail(detail), options);

    this.name = 'RequestError';
    this.detail = detail;
  }
}

export const isRetryableStatus = (status: number): boolean =>
  status === statusByErrorCode.RATE_LIMITED ||
  status >= statusByErrorCode.INTERNAL_ERROR;

export const isUnauthorized = (error: unknown): error is RequestError =>
  error instanceof RequestError &&
  error.detail.kind === 'server' &&
  error.detail.status === statusByErrorCode.UNAUTHORIZED;

export const isErrorBody = (error: unknown): error is TErrorBody =>
  isRecord(error) &&
  !(error instanceof Error) &&
  typeof error.code === 'string';

export const getErrorReason = (error: unknown): string | undefined => {
  if (isErrorBody(error)) {
    return error.reason;
  }

  if (!(error instanceof RequestError)) {
    return undefined;
  }

  const { detail } = error;

  if (detail.kind === 'server') {
    return detail.body?.reason;
  }

  return detail.kind === 'refused' ? detail.reason : undefined;
};

export const isUnexpectedError = (error: unknown): boolean => {
  if (!(error instanceof RequestError)) {
    return true;
  }

  const { detail } = error;

  switch (detail.kind) {
    case 'server':
      return detail.status >= statusByErrorCode.INTERNAL_ERROR;
    case 'decode':
      return true;
    default:
      return false;
  }
};
